import {
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  SelectChangeEvent,
  Stack,
} from "@mui/material";
import { useFormik } from "formik";
import produce from "immer";
import React from "react";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomSelect,
  CustomTextField,
} from "../../../../../../../components/formsComponents";
import { renderDropdownValue } from "../../../../../../../helpers";
import {
  policyStatus,
} from "../../../../../../../constants/constants";
import {
  ILocationResponsePayload,
} from "../../../../../../../components/LocationAutoComplete/LocationAutoComplete";
import {
  CustomDatePicker,
} from "../../../../../../../components/formsComponents/CustomDatePicker";
import {
  useCustomerFullInfo,
  useCustomerPolicyDetails,
  useCustomerPropspect,
  useDispatchWrapper,
  useFetchPolicies,
} from "../../../../../../../hooks";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../../redux/initialStoreState";
import { LabelCard } from "../../../../../../../components/LabelCard/LabelCard";
import { saveCustomerPolicyDetailsAsync } from "../../../../../../../redux/customerPolicyDetails/customerPolicyDetailsActions";
import { ICustomerPolicyDetails } from "../../../../../../../redux/customerPolicyDetails/customerPolicyDetails.types";
import { MODULE_IDS, POLICIES, ROLES } from "../../../../../../../constants/enums";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RoleBasedCustomButton } from "../../../../../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import { showMessage } from "../../../../../../../redux/messages/messagesActions";
import { AddressSearchAutoComplete } from "../../../../../../../components/AddressAutoComplete/AddressSearchAutoComplete";
import { IMergedFormik, LifeInsurancePolicyAdmin } from "../PolicyAdmin/PolicyAdmin";
import { BinderNumber } from "../../../../../../../components/BinderNumber/BinderNumber";
import { useTabsUncontrolledContext } from "../../../../../../../contexts/TabsContextProvider/TabsContextUncontrolledProvider";
import { isEqual } from "lodash";
import { useRoles } from "../../../../../../../security/RolesProvider/RolesProvider";

/**
 * The merged customer-policy-details record drives the whole Life Insurance
 * customer-info screen. Life carries two extra persisted fields that are not
 * part of the shared canonical contract (`policy_term`) plus the derived
 * `compliance_status` value shown on this form; they are added here so the
 * single merged Formik can read and write them without losing their values.
 */
export type ILifeCustomerPolicyDetails = ICustomerPolicyDetails & {
  policy_term: string | null;
  compliance_status?: string | null;
};

export const LifeInsuranceCustomerInfo: React.FC<{
  customerId?: number;
  customerPolicyId?: number | null;
}> = (props) => {
  const { customerId, customerPolicyId } = props;
  const isUpdate = customerPolicyId ? true : false;
  const { policies, getPolicySubtypes } = useFetchPolicies();
  const customerFullDetails = useCustomerFullInfo();
  const customerProspect = useCustomerPropspect();
  const { details } = useCustomerPolicyDetails(
    customerPolicyId ? Number(customerPolicyId) : undefined
  );
  const [saveLoading, setSaveLoading] = React.useState(false);

  const ComplianceCheckList = useSelector(
    (storeState: IStoreState) =>
      storeState.lifeInsurance.complianceCheckList.data
  );

  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { roleName } = useRoles();
  const [buttonClicked, setButtonClicked] = React.useState<string | null>(null);
  const { changeStep } = useTabsUncontrolledContext();
  const customerPolicyIdRef = React.useRef(customerPolicyId);
  const {
    values,
    handleChange,
    handleSubmit,
    setValues,
    setFieldValue,
    errors,
    submitForm,
    ...restFormik
  } = useFormik<ILifeCustomerPolicyDetails>({
    initialValues: {
      ...(details.data as ILifeCustomerPolicyDetails),
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: any = {};
      if (Number(values.policy_sub_type) === 0) {
        errors.policy_sub_type = "*Line of Business is required";
      } else if (!values.line_of_business) {
        errors.line_of_business = "*Line of Business is required";
      }
      return errors;
    },
    onSubmit: async (values) => {
      return new Promise((resolve, reject) => {
        setSaveLoading(true);
        dispatch(
          saveCustomerPolicyDetailsAsync(
            { ...values, customer_id: customerId as number },
            details.data,
            isUpdate,
            (isSuccess, policyId, error) => {
              if (error) {
                reject(error);
              }
              if (isSuccess && policyId) {
                customerPolicyIdRef.current = policyId;
                resolve(policyId);
              }
              setSaveLoading(false);
            }
          )
        );
      });
    },
  });

  // A single merged Formik now drives the customer info, policy admin, and
  // premium sections. The PolicyAdmin/Premium subcomponent binds to this same
  // Formik instance for both its `policyAdmin` and `policyPremimum` props,
  // since all fields now live on one canonical merged object.
  const mergedFormik = {
    values,
    handleChange,
    setFieldValue,
    setValues,
    errors,
    ...restFormik,
  };

  /**Handle Final Submit */
  const handleFinalSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      let saveResult: any = true;

      if (isUpdate) {
        // Single change-detection gate: only save when the current form values
        // differ from the persisted merged Customer_Policy_Details.
        const detailsChanged = !isEqual(
          { ...values, customer_id: customerId },
          { ...details.data, customer_id: customerId }
        );

        if (detailsChanged) {
          saveResult = await submitForm();
        }
      } else {
        saveResult = await submitForm();
      }

      if (saveResult) {
        navigate(
          `/life-insurance-policy/${customerId}/basic-details/${customerPolicyIdRef.current}`
        );
        if (buttonClicked === "saveAndNext") {
          changeStep(1);
        }
        dispatch(
          showMessage({
            type: "success",
            message: "Customer policy information saved successfully!",
            displayAs: "snackbar",
          })
        );
        // Reload the page after a successful save so the latest persisted
        // data is reflected across all tabs/sections.
        window.location.reload();
      }
    } catch (error: any) {
      dispatch(
        showMessage({
          type: "error",
          message: error.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      setSaveLoading(false); // Stop loading indicator
    }
  };

  const handleCheckBox =
    (key: keyof ILifeCustomerPolicyDetails) =>
    (e: React.SyntheticEvent<Element>, checked: boolean) => {
      setFieldValue(key, checked);
    };

  const handleLineOfBusiness = (event: SelectChangeEvent<unknown>) => {
    setValues({
      ...values,
      line_of_business: event.target.value as string,
      policy_sub_type: "",
    });
  };

  const handleBinderSave = (
    binderNumber: string | null,
    onCallBack: (isSuccess: boolean) => void
  ) => {
    dispatch(
      saveCustomerPolicyDetailsAsync(
        {
          ...values,
          customer_id: customerId as number,
          binder_number: binderNumber,
        },
        details.data,
        isUpdate,
        (isSuccess) => {
          onCallBack(isSuccess);
        }
      )
    );
  };

  const handlePolicyStatusChange = (event: SelectChangeEvent<unknown>) => {
    const { value } = event.target;

    // Safely cast value to string
    const selectedValue = value as string;

    const newValues = produce(
      values,
      (draftValues: ILifeCustomerPolicyDetails) => {
        draftValues.current_policy_status = selectedValue;
        draftValues.binder_number =
          selectedValue === "Prospect" ? null : values.binder_number;
        draftValues.policy_number =
          selectedValue === "Prospect" ? null : values.policy_number;
        if (selectedValue === "Voided") {
          draftValues.cancellation_reason = "NT: Not Taken";
          draftValues.date_cancelled = values.effective_date;
          draftValues.cancellation_type = "Flat";
          draftValues.policy_status = "Voided";
        }
      }
    );

    setValues(newValues);
  };

  const handleAddress = (data: ILocationResponsePayload) => {
    setValues((prevValues: any) => ({
      ...prevValues, // Ensure previous state is maintained
      address: data.address,
      city: data.city,
      country: data.country,
      province_or_state: data.state,
      postal_code: data.postalCode,
    }));
  };

  const isInitialized = React.useRef(false);

  React.useEffect(() => {
    if (!customerPolicyId && customerFullDetails && !isInitialized.current) {
      const urlTransactionType = searchParams.get("transaction_type");
      setValues((prevValues: any) => ({
        ...prevValues,
        customer_code: customerProspect.data.customer_code,
        customer_name: customerProspect.data.customer_name,
        address: prevValues.address || customerProspect.data.mailing_address,
        province_or_state:
          prevValues.province_or_state ||
          customerProspect.data.province_or_state,
        city: prevValues.city || customerProspect.data.city,
        postal_code:
          prevValues.postal_code || customerProspect.data.postal_code,
        country: prevValues.country || customerProspect.data.country,
        unit_or_suite:
          prevValues.unit_or_suite || customerProspect.data.unit_or_suite,
        language: prevValues.language || customerProspect.data.language,
        line_of_business: POLICIES.LIFE_AND_FINCANCIAL_POLICY,
        // Seed the merged policy-admin defaults from the customer prospect so
        // branch/csr/marketer auto-populate for a new policy. Seeding lives on
        // the single owning Formik (CustomerInfo) to avoid a race with a second
        // seeding effect in the PolicyAdmin subcomponent.
        branch_code: customerProspect.data.branch_code,
        csr: customerProspect.data.csr,
        marketer: customerProspect.data.marketer,
        ...(urlTransactionType ? { transaction_type: urlTransactionType } : {}),
      }));
      isInitialized.current = true;
    } else if (customerPolicyId) {
      setValues((prevValues: any) => ({
        ...prevValues,
        ...(details.data as ILifeCustomerPolicyDetails),
      }));
    }

     
  }, [details.data, customerProspect.data, customerPolicyId]);

  React.useEffect(() => {
    setFieldValue("compliance_status", ComplianceCheckList.compliance_status);
  }, [ComplianceCheckList]);

  return (
    <form onSubmit={handleFinalSubmit}>
      <Grid container spacing={1}>
        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Life Insured</CustomFormLabel>
          <CustomTextField
            name="customer_name"
            variant="outlined"
            size="small"
            type="text"
            fullWidth
            value={values.customer_name}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} lg={1.8}>
          <CustomFormLabel>Customer Code</CustomFormLabel>
          <CustomTextField
            name="customer_code"
            variant="outlined"
            size="small"
            type="text"
            fullWidth
            disabled
            value={values.customer_code}
          />
        </Grid>

        <Grid item xs={12} lg={1.8}>
          <CustomFormLabel>Language</CustomFormLabel>
          <ControlledCustomSelect
            fullWidth
            value={values.language}
            name="language"
            onChange={handleChange}
            placeholder="Select One"
            displayEmpty
            options={["E", "F"].map((template) => {
              return { label: template, value: template };
            })}
          ></ControlledCustomSelect>
        </Grid>

        <Grid item xs={12} lg={1.8}>
          <CustomFormLabel>Transaction Type</CustomFormLabel>
          <ControlledCustomSelect
            fullWidth
            disabled={roleName !== ROLES.ADMIN}
            value={values.transaction_type}
            name="transaction_type"
            onChange={handleChange}
            placeholder="Select One"
            displayEmpty
            options={[
              { label: "New", value: "New" },
              { label: "Remarket", value: "Remarket" },
            ]}
          ></ControlledCustomSelect>
        </Grid>

        <Grid item xs={12} lg={1.8}>
          <CustomFormLabel>Address Type</CustomFormLabel>
          <CustomSelect
            variant="outlined"
            size="small"
            type="string"
            name="residential_or_business"
            fullWidth
            value={values.residential_or_business}
            onChange={handleChange}
            displayEmpty
            renderValue={renderDropdownValue(`Select one`)}
          >
            {["R", "B"].map((transactionType) => {
              return (
                <MenuItem value={transactionType}>{transactionType}</MenuItem>
              );
            })}
          </CustomSelect>
        </Grid>

        <Grid item xs={12} lg={3.6}>
          <AddressSearchAutoComplete
            address={values.address}
            onAddressChange={handleAddress}
          />
        </Grid>

        <Grid item xs={12} lg={1.8}>
          <CustomFormLabel>Unit/Suite</CustomFormLabel>
          <CustomTextField
            id="unit_or_suite"
            variant="outlined"
            size="small"
            type="text"
            fullWidth
            value={values.unit_or_suite}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} lg={1.8}>
          <CustomFormLabel>City</CustomFormLabel>
          <CustomTextField
            id="city"
            variant="outlined"
            size="small"
            type="text"
            fullWidth
            value={values.city}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} lg={1.8}>
          <CustomFormLabel>State/Province</CustomFormLabel>
          <CustomTextField
            id="province_or_state"
            variant="outlined"
            size="small"
            type="text"
            fullWidth
            value={values.province_or_state}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} lg={1.2}>
          <CustomFormLabel>Postal Code</CustomFormLabel>
          <CustomTextField
            id="postal_code"
            variant="outlined"
            size="small"
            type="text"
            fullWidth
            value={values.postal_code}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} lg={1.8}>
          <CustomFormLabel>Country</CustomFormLabel>
          <CustomTextField
            id="country"
            variant="outlined"
            size="small"
            type="text"
            fullWidth
            value={values.country}
            onChange={handleChange}
          />
        </Grid>
      </Grid>

      <LabelCard heading="Policy Information" />

      <Grid container spacing={1}>
     
        {values.current_policy_status !== "Prospect" && (
          <BinderNumber
            value={values.binder_number}
            policyId={customerPolicyId}
            onSave={handleBinderSave}
            girdSize={2.4}
          />
        )}

        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Policy Number</CustomFormLabel>
          <CustomTextField
            name="policy_number"
            variant="outlined"
            size="small"
            type="text"
            fullWidth
            value={values.policy_number}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Policy Term</CustomFormLabel>
          <ControlledCustomSelect
            variant="outlined"
            size="small"
            type="string"
            name="policy_term"
            fullWidth
            value={values.policy_term}
            onChange={handleChange}
            displayEmpty
            placeholder="Select One"
            options={["T10", "T15", "T20", "T25", "T30", "T35", "T40", "T100", "L10", "L20", "L65", "L100", "Custom Term"].map((term) => {
              return { label: term, value: term };
            })}
          ></ControlledCustomSelect>
           
        </Grid>

        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Effective Date</CustomFormLabel>
          <CustomDatePicker
            fullWidth
            value={values.effective_date || ""}
            onChange={(newValue) => {
              setFieldValue("effective_date", newValue);
            }}
          />
        </Grid>

        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Expiry Date</CustomFormLabel>
          <CustomDatePicker
            fullWidth
            value={values.expiry_date || ""}
            onChange={(newValue) => {
              setFieldValue("expiry_date", newValue);
            }}
          />
        </Grid>

        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Policy Limit</CustomFormLabel>
          <CustomTextField
            name="policy_limit"
            variant="outlined"
            size="small"
            type="text"
            fullWidth
            value={values.policy_limit}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Policy Type</CustomFormLabel>
          <CustomSelect
            variant="outlined"
            size="small"
            type="string"
            name="line_of_business"
            fullWidth
            value={values.line_of_business}
            onChange={handleLineOfBusiness}
            disabled
            displayEmpty
            renderValue={renderDropdownValue(`Select one`)}
            error={Boolean(errors.line_of_business)}
          >
            {policies.map((policy) => {
              return <MenuItem value={policy.type}>{policy.type}</MenuItem>;
            })}
          </CustomSelect>
        </Grid>

        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Line of Business</CustomFormLabel>
          <ControlledCustomSelect
            variant="outlined"
            size="small"
            type="string"
            name="policy_sub_type"
            fullWidth
            value={values.policy_sub_type}
            onChange={handleChange}
            displayEmpty
            placeholder="Select One"
            // helperText={errors.policy_sub_type}
            options={getPolicySubtypes(values.line_of_business || "").map(
              (item) => {
                return { label: item.name, value: item.name };
              }
            )}
            error={Boolean(errors.policy_sub_type)}
          ></ControlledCustomSelect>
        </Grid>

        {values.policy_status === "Cancelled" && (
          <>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Date Cancelled</CustomFormLabel>
              <CustomDatePicker
                fullWidth
                value={values.date_cancelled || ""}
                onChange={(newValue) => {
                  setFieldValue("date_cancelled", newValue);
                }}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Policy Status</CustomFormLabel>
          <ControlledCustomSelect
            variant="outlined"
            size="small"
            type="string"
            name="current_policy_status"
            fullWidth
            value={values.current_policy_status}
            onChange={handlePolicyStatusChange}
            displayEmpty
            placeholder="Select one"
            options={[
              "Active",
              "Cancelled",
              "Lapsed",
              "Prospect",
              "Voided",
            ].map((status) => {
              return { label: status, value: status };
            })}
          ></ControlledCustomSelect>
        </Grid>

        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Policy Transaction</CustomFormLabel>
          <CustomSelect
            variant="outlined"
            size="small"
            type="string"
            fullWidth
            name="policy_status"
            value={values.policy_status}
            onChange={handleChange}
            displayEmpty
            renderValue={renderDropdownValue(`Select one`)}
          >
            {policyStatus.map((status) => {
              return <MenuItem value={status}>{status}</MenuItem>;
            })}
          </CustomSelect>
        </Grid>

        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Remarketed</CustomFormLabel>
          <ControlledCustomSelect
            fullWidth
            value={values.remarketed}
            name="remarketed"
            onChange={handleChange}
            placeholder="Select One"
            displayEmpty
            options={[
              { label: "Yes", value: "Y" },
              { label: "No", value: "N" },
            ]}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          {/*
            The subcomponent binds to the canonical ICustomerPolicyDetails shape.
            The merged Formik carries the Life-extended shape (adds policy_term /
            compliance_status), which is a structural superset; cast at this
            boundary so the extra Life-only fields are preserved while the admin
            and premium fields bind through the same single Formik instance.
          */}
          <LifeInsurancePolicyAdmin
            customerpolicyId={customerPolicyId}
            policyAdmin={mergedFormik as unknown as IMergedFormik}
            policyPremimum={mergedFormik as unknown as IMergedFormik}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <LabelCard heading="Preferences" />
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={12} lg={4}>
            <FormControlLabel
              labelPlacement="end"
              onChange={handleCheckBox(
                "send_invoices_renewal_receivable_via_email"
              )}
              control={
                <Checkbox
                  checked={
                    values.send_invoices_renewal_receivable_via_email || false
                  }
                />
              }
              label="Send Invoices , Renewal , Receivable By Email"
              sx={{ m: 0, fontSize: 15 }}
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <FormControlLabel
              labelPlacement="end"
              onChange={handleCheckBox("send_statements_via_email")}
              control={
                <Checkbox
                  name="send_statements_via_email"
                  checked={values.send_statements_via_email || false}
                />
              }
              label="Send Statement By Email"
              sx={{ m: 0, fontSize: 15 }}
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <FormControlLabel
              labelPlacement="end"
              onChange={handleCheckBox("send_edocs_via_email")}
              control={
                <Checkbox
                  name="send_edocs_via_email"
                  checked={values.send_edocs_via_email || false}
                />
              }
              label="Send Docs By Email"
              sx={{ m: 0, fontSize: 15 }}
            />
          </Grid>
        </Grid>
      </Grid>

      <Stack direction={"row"} spacing={2} sx={{ mt: 2 }}>
        <RoleBasedCustomButton
          moduleKey={MODULE_IDS.LIFE_AND_FINCANCIAL}
          type="submit"
          disabled={saveLoading}
          variant="contained"
          onClick={() => setButtonClicked("save")}
        >
          Save
        </RoleBasedCustomButton>
        {/* <RoleBasedCustomButton
          moduleKey={MODULE_IDS.LIFE_AND_FINCANCIAL}
          type="submit"
          disabled={saveLoading}
          variant="contained"
          onClick={() => setButtonClicked("saveAndNext")}
        >
          Save & Next
        </RoleBasedCustomButton> */}
      </Stack>
    </form>
  );
};
